import type { ChallengeEditorSectionHandle } from '@/components/challenges/editor/types';
import type { OwnerScopeProps } from '@/components/shared/ownerScope';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Save, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface OwnerCollectibleSummary {
  id: string;
  name: string;
  iconUrl: string | null;
}

interface RewardLink {
  collectibleId: string;
  quantity: number;
}

interface RequirementLink {
  collectibleId: string;
  quantityNeeded: number;
}

interface Props extends Omit<OwnerScopeProps, 'structureEditable'> {
  /** Event challenge id or template challenge-link id, per the owner scope. */
  challengeId: string;
  /**
   * `standalone` (default) renders the section's own Save buttons + toasts.
   * `controlled` hides them and exposes `{ isDirty, save }` via
   * `onRegisterHandle` so a parent editor's save pipeline drives persistence.
   */
  mode?: 'standalone' | 'controlled';
  /** Required in controlled mode: receives the save handle (null on unmount). */
  onRegisterHandle?: (handle: ChallengeEditorSectionHandle | null) => void;
}

/** Order-sensitive structural equality via JSON (rows aren't reorderable). */
function sameLinks<T>(a: readonly T[], b: readonly T[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Awards + requirements editor for a single challenge in an owner scope
 * (event or template). Loads the owner's collectibles + the challenge's
 * existing links, and PUTs the full replacement lists — via its own Save
 * buttons (standalone) or the parent editor's save pipeline (controlled).
 */
export function ChallengeCollectiblesSection({
  ownerId,
  apiBase,
  keyPrefix,
  challengeId,
  mode = 'standalone',
  onRegisterHandle,
}: Props) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const isStandalone = mode === 'standalone';
  const ownerNoun = keyPrefix === 'Event' ? 'event' : 'template';

  // Owner collectibles available to link.
  const { data: catalogResp, isLoading: catalogLoading } = useQuery<{
    success: boolean;
    data: OwnerCollectibleSummary[];
  }>({
    queryKey: [`${keyPrefix}Collectibles`, ownerId],
    queryFn: () => apiClient.get(`${apiBase}/collectibles`),
    enabled: !!ownerId,
  });
  const catalog = catalogResp?.data ?? [];

  const { data: rewardsResp } = useQuery<{
    success: boolean;
    data: Array<{
      id: string;
      quantity: number;
      collectible: OwnerCollectibleSummary;
    }>;
  }>({
    queryKey: ['ChallengeCollectibleRewards', ownerId, challengeId],
    queryFn: () =>
      apiClient.get(
        `${apiBase}/challenges/${challengeId}/collectible-rewards`,
      ),
    enabled: !!ownerId && !!challengeId,
  });

  const { data: requirementsResp } = useQuery<{
    success: boolean;
    data: Array<{
      id: string;
      quantityNeeded: number;
      collectible: OwnerCollectibleSummary;
    }>;
  }>({
    queryKey: ['ChallengeCollectibleRequirements', ownerId, challengeId],
    queryFn: () =>
      apiClient.get(
        `${apiBase}/challenges/${challengeId}/collectible-requirements`,
      ),
    enabled: !!ownerId && !!challengeId,
  });

  const [rewards, setRewards] = useState<RewardLink[]>([]);
  const [requirements, setRequirements] = useState<RequirementLink[]>([]);

  // Baseline + latest-state refs so the controlled-mode handle can read
  // current dirty state without re-registering on every change.
  const rewardsBaselineRef = useRef<RewardLink[]>([]);
  const requirementsBaselineRef = useRef<RequirementLink[]>([]);
  const rewardsRef = useRef<RewardLink[]>(rewards);
  rewardsRef.current = rewards;
  const requirementsRef = useRef<RequirementLink[]>(requirements);
  requirementsRef.current = requirements;

  useEffect(() => {
    if (rewardsResp?.data) {
      const next = rewardsResp.data.map((r) => ({
        collectibleId: r.collectible.id,
        quantity: r.quantity,
      }));
      setRewards(next);
      rewardsBaselineRef.current = next;
    }
  }, [rewardsResp]);

  useEffect(() => {
    if (requirementsResp?.data) {
      const next = requirementsResp.data.map((r) => ({
        collectibleId: r.collectible.id,
        quantityNeeded: r.quantityNeeded,
      }));
      setRequirements(next);
      requirementsBaselineRef.current = next;
    }
  }, [requirementsResp]);

  const saveRewardsMutation = useMutation({
    mutationFn: async (payload: RewardLink[]) =>
      apiClient.put(
        `${apiBase}/challenges/${challengeId}/collectible-rewards`,
        { rewards: payload },
      ),
    onSuccess: (_resp, payload) => {
      rewardsBaselineRef.current = payload;
      queryClient.invalidateQueries({
        queryKey: ['ChallengeCollectibleRewards', ownerId, challengeId],
      });
      if (isStandalone) toast.success('Rewards saved');
    },
    onError: (err: Error) => {
      if (isStandalone) toast.error(err?.message || 'Failed to save rewards');
    },
  });

  const saveRequirementsMutation = useMutation({
    mutationFn: async (payload: RequirementLink[]) =>
      apiClient.put(
        `${apiBase}/challenges/${challengeId}/collectible-requirements`,
        { requirements: payload },
      ),
    onSuccess: (_resp, payload) => {
      requirementsBaselineRef.current = payload;
      queryClient.invalidateQueries({
        queryKey: ['ChallengeCollectibleRequirements', ownerId, challengeId],
      });
      if (isStandalone) toast.success('Requirements saved');
    },
    onError: (err: Error) => {
      if (isStandalone) toast.error(err?.message || 'Failed to save requirements');
    },
  });

  const saveRewardsRef = useRef(saveRewardsMutation);
  saveRewardsRef.current = saveRewardsMutation;
  const saveRequirementsRef = useRef(saveRequirementsMutation);
  saveRequirementsRef.current = saveRequirementsMutation;

  useEffect(() => {
    if (!onRegisterHandle) return undefined;
    onRegisterHandle({
      label: 'collectibles',
      isDirty: () =>
        !sameLinks(rewardsRef.current, rewardsBaselineRef.current) ||
        !sameLinks(requirementsRef.current, requirementsBaselineRef.current),
      save: async () => {
        if (!sameLinks(rewardsRef.current, rewardsBaselineRef.current)) {
          await saveRewardsRef.current.mutateAsync(rewardsRef.current);
        }
        if (!sameLinks(requirementsRef.current, requirementsBaselineRef.current)) {
          await saveRequirementsRef.current.mutateAsync(requirementsRef.current);
        }
      },
    });
    return () => onRegisterHandle(null);
  }, [onRegisterHandle]);

  if (catalogLoading) {
    return <Skeleton className="h-48 rounded-xl" />;
  }

  if (catalog.length === 0) {
    return (
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Collectibles
          </CardTitle>
          <CardDescription>
            Add collectibles to this {ownerNoun} before linking them to challenges.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">
            No {ownerNoun} collectibles found. Visit the {ownerNoun}&apos;s
            &quot;Collectibles&quot; tab to add some.
          </p>
        </CardContent>
      </Card>
    );
  }

  const collectibleNameById = new Map(catalog.map((c) => [c.id, c.name]));
  const availableForReward = catalog.filter(
    (c) => !rewards.some((r) => r.collectibleId === c.id),
  );
  const availableForRequirement = catalog.filter(
    (c) => !requirements.some((r) => r.collectibleId === c.id),
  );

  return (
    <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
      <CardHeader className="px-6 py-4 !pb-4 border-b">
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Collectibles
        </CardTitle>
        <CardDescription>
          Award collectibles when this challenge is completed, or require
          collectibles before this challenge unlocks.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-8">
        {/* Awards */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Awards on completion</h4>
            {isStandalone && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveRewardsMutation.mutate(rewards)}
                loading={saveRewardsMutation.isPending}
              >
                <Save className="h-3.5 w-3.5" />
                Save Awards
              </Button>
            )}
          </div>

          {rewards.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Completing this challenge awards no collectibles.
            </p>
          )}

          <div className="space-y-2">
            {rewards.map((row, idx) => (
              <div
                key={`${row.collectibleId}-${idx}`}
                className="flex items-center gap-2"
              >
                <div className="flex-1 text-sm font-medium">
                  {collectibleNameById.get(row.collectibleId) ?? row.collectibleId}
                </div>
                <Input
                  type="number"
                  min={1}
                  value={row.quantity}
                  onChange={(e) =>
                    setRewards((prev) =>
                      prev.map((r, i) =>
                        i === idx
                          ? { ...r, quantity: parseInt(e.target.value) || 1 }
                          : r,
                      ),
                    )
                  }
                  className="w-24"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setRewards((prev) => prev.filter((_, i) => i !== idx))
                  }
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          {availableForReward.length > 0 && (
            <Select
              onValueChange={(value) => {
                setRewards((prev) => [
                  ...prev,
                  { collectibleId: value, quantity: 1 },
                ]);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Add a collectible to award..." />
              </SelectTrigger>
              <SelectContent>
                {availableForReward.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </section>

        {/* Requirements */}
        <section className="space-y-3 border-t pt-6">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Requirements to unlock</h4>
            {isStandalone && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveRequirementsMutation.mutate(requirements)}
                loading={saveRequirementsMutation.isPending}
              >
                <Save className="h-3.5 w-3.5" />
                Save Requirements
              </Button>
            )}
          </div>

          {requirements.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No collectibles required to unlock this challenge.
            </p>
          )}

          <div className="space-y-2">
            {requirements.map((row, idx) => (
              <div
                key={`${row.collectibleId}-${idx}`}
                className="flex items-center gap-2"
              >
                <div className="flex-1 text-sm font-medium">
                  {collectibleNameById.get(row.collectibleId) ?? row.collectibleId}
                </div>
                <Input
                  type="number"
                  min={1}
                  value={row.quantityNeeded}
                  onChange={(e) =>
                    setRequirements((prev) =>
                      prev.map((r, i) =>
                        i === idx
                          ? {
                              ...r,
                              quantityNeeded: parseInt(e.target.value) || 1,
                            }
                          : r,
                      ),
                    )
                  }
                  className="w-24"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setRequirements((prev) => prev.filter((_, i) => i !== idx))
                  }
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          {availableForRequirement.length > 0 && (
            <Select
              onValueChange={(value) => {
                setRequirements((prev) => [
                  ...prev,
                  { collectibleId: value, quantityNeeded: 1 },
                ]);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Add a required collectible..." />
              </SelectTrigger>
              <SelectContent>
                {availableForRequirement.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
